// Libraries
import { useTranslation } from "react-i18next";
import { BlobServiceClient } from "@azure/storage-blob";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

// Services
import { AccessInfoType, ReportInfoType } from "src/services/exams/types";

// Contexts
import { useClinicContext } from "src/contexts/clinic/ClinicProvider";
import { useSnackbarContext } from "src/contexts/alerts/SnackbarContext";

// Constants
import {
  ReportFileType,
  reportFileTypes,
  ReportStatus,
} from "src/constants/enums";

// Types
import { FileInfoType } from "src/types/sharedTypes";

type UseReportGenerationType = {
  getAccessInfo: (fileType: ReportFileType) => Promise<AccessInfoType>;
  generateReportFile: (fileType: ReportFileType) => Promise<void>;
  getReportInfo: () => Promise<Array<ReportInfoType>>;
};

const initialFileInfo: FileInfoType = {
  hasBlobStorageError: false,
  isDownloading: false,
  isPrinting: false,
  accessInfo: null,
  reportInfoData: null,
};

// Which fileInfo flag reflects a given blob fetch's in-progress state --
// kept separate so downloading and printing don't show each other's loader.
type BlobFetchStateKey = "isDownloading" | "isPrinting";

const useReportGeneration = ({
  getAccessInfo,
  generateReportFile,
  getReportInfo,
}: UseReportGenerationType) => {
  const { t } = useTranslation();
  const { clinicInfo } = useClinicContext();
  const { setSnackbarInfo } = useSnackbarContext();

  const [fileInfo, setFileInfo] = useState<{
    [key in ReportFileType]: FileInfoType;
  }>({
    PDF: { ...initialFileInfo },
    DICOM: { ...initialFileInfo },
    PNG: { ...initialFileInfo },
  });

  const isfirstLoading = useRef(true);

  // true when the report info request fails
  const [reportInfoError, setReportInfoError] = useState(false);

  // controls the download button display
  const showDICOMButton = useMemo(
    () => clinicInfo?.licensing?.canUseDicomReports,
    [clinicInfo?.licensing?.canUseDicomReports]
  );
  const showPNGButton = useMemo(
    () => clinicInfo?.licensing?.canUsePngReports,
    [clinicInfo?.licensing?.canUsePngReports]
  );

  // Retrieve access information for blob storage
  const getBlobAccessInfo = useCallback(
    async (fileType: ReportFileType) => {
      try {
        const accessInfo = await getAccessInfo(fileType);

        setFileInfo((prev) => {
          const newFileInfo = { ...prev };
          if (newFileInfo?.[fileType]?.hasBlobStorageError !== undefined)
            newFileInfo[fileType].hasBlobStorageError = false;
          if (newFileInfo?.[fileType]?.accessInfo !== undefined)
            newFileInfo[fileType].accessInfo = accessInfo;
          return newFileInfo;
        });

        return accessInfo;
      } catch (e) {
        if (fileInfo?.[fileType]?.hasBlobStorageError) {
          setFileInfo((prev) => {
            const newFileInfo = { ...prev };
            if (newFileInfo?.[fileType]?.hasBlobStorageError)
              newFileInfo[fileType].hasBlobStorageError = true;
            return newFileInfo;
          });
        }
        throw e;
      }
    },
    [fileInfo, getAccessInfo]
  );

  const getReportInfoData = useCallback(async () => {
    try {
      const reportInfo = await getReportInfo();
      setReportInfoError(false);

      if (reportInfo?.length > 0) {
        // checks if the specific file format needs to be generated or not
        // each property is true when the report doesn't have info or the file type failed to be generated
        const fileTypeStatusChecker: { [key in ReportFileType]: boolean } = {
          PNG: false,
          DICOM: false,
          PDF: false,
        };

        const existingFileTypes: {
          [key in ReportFileType]?: boolean;
        } = {};

        // get each file type's info from the getReportInfo response
        reportInfo.forEach((fileData) => {
          const fileType = fileData?.type as ReportFileType;

          if (reportFileTypes.includes(fileType)) {
            setFileInfo((prev) => {
              const newFileInfo = { ...prev };
              if (newFileInfo?.[fileType]?.reportInfoData !== undefined) {
                newFileInfo[fileType].reportInfoData = fileData;
              }
              return newFileInfo;
            });

            existingFileTypes[fileType] = true;

            // set file types to be generated
            if (
              fileData.status === ReportStatus.Failed ||
              fileData.status === ReportStatus.Unavailable
            ) {
              fileTypeStatusChecker[fileType] = true;
            }
            // else file types don't need to be generated
            else if (fileData.status !== ReportStatus.Ready)
              fileTypeStatusChecker[fileType] = false;
          }
        });

        // Regenerate missing files on demand only once
        if (isfirstLoading.current === false) return;

        // get file types that had no information from the response
        reportFileTypes.forEach((type) => {
          if (!existingFileTypes[type]) fileTypeStatusChecker[type] = true;
        });

        // Generate files that were never generated or had Unavailable/Failed statuses
        reportFileTypes.forEach(async (type) => {
          if (fileTypeStatusChecker[type]) {
            // check if the license exists before generating the files
            const isDicomDisabled = type === "DICOM" && !showDICOMButton;
            const isPngDisabled = type === "PNG" && !showPNGButton;
            if (isDicomDisabled || isPngDisabled) return;

            await generateReportFile(type);
          }
        });
      }
    } catch (e) {
      const ErrorMessages = {
        ReportInfoError: "Report info Error",
        ReportGenerationError: "Failed to generate",
      };

      if (e === ErrorMessages.ReportInfoError) {
        setReportInfoError(true);
      } else if (e === ErrorMessages.ReportGenerationError) {
        console.error(e);
      } else {
        console.error(e);
      }
    } finally {
      isfirstLoading.current = false;
    }
  }, [showDICOMButton, showPNGButton, generateReportFile, getReportInfo]);

  // Check continuously if blob info to download pdf is ready or not
  useEffect(() => {
    const intervalTime = 3000; // Interval between requests (3 seconds)

    const intervalId = setInterval(async () => {
      const isDicomDone =
        fileInfo.DICOM.reportInfoData?.status === ReportStatus.Ready ||
        !showDICOMButton;
      const isPngDone =
        fileInfo.PNG.reportInfoData?.status === ReportStatus.Ready ||
        !showPNGButton;
      const isPdfDone =
        fileInfo.PDF.reportInfoData?.status === ReportStatus.Ready;

      if (isDicomDone && isPngDone && isPdfDone) {
        clearInterval(intervalId);
        return;
      }

      try {
        await getReportInfoData();
        setReportInfoError(false);
      } catch (e) {
        setReportInfoError(true);
      }
    }, intervalTime);

    return () => {
      clearInterval(intervalId);
    };
  }, [
    fileInfo.DICOM.reportInfoData?.status,
    fileInfo.PDF.reportInfoData?.status,
    fileInfo.PNG.reportInfoData?.status,
    showPNGButton,
    showDICOMButton,
    getReportInfoData,
  ]);

  const setBlobFetchState = (
    fileType: ReportFileType,
    stateKey: BlobFetchStateKey,
    value: boolean
  ) => {
    setFileInfo((prev) => {
      const newFileInfo = { ...prev };
      if (newFileInfo?.[fileType]?.[stateKey] !== undefined)
        newFileInfo[fileType][stateKey] = value;
      return newFileInfo;
    });
  };

  const downloadPdfBlob = async (
    fileType: ReportFileType,
    stateKey: BlobFetchStateKey = "isDownloading"
  ) => {
    setBlobFetchState(fileType, stateKey, true);

    try {
      const accessInfo = await getBlobAccessInfo(fileType);
      const access = accessInfo?.access;

      if (!accessInfo || !access) {
        setBlobFetchState(fileType, stateKey, false);
        throw new Error("Invalid access information");
      }

      const blobServerClient = new BlobServiceClient(
        `${access?.url}${access.sasKey}`
      );
      const containerClient = blobServerClient.getContainerClient(
        access.containerName
      );
      const blobClient = containerClient
        .getBlobClient(accessInfo.fileName)
        .getBlockBlobClient();

      const downloadedRes = await blobClient.download();
      const blob = await downloadedRes.blobBody;

      if (blob) {
        setBlobFetchState(fileType, stateKey, false);
        return {
          blob: blob,
          fileName: accessInfo.recommendedFileName,
        };
      }
      return null;
    } catch (e) {
      // display error toast if PDF download fails
      setSnackbarInfo({
        variant: "error",
        message: t("Failed to download PDF!"),
      });
      console.error(e);
      return null;
    } finally {
      setBlobFetchState(fileType, stateKey, false);
    }
  };

  const downloadFile = async (fileType: ReportFileType) => {
    const blobData = await downloadPdfBlob(fileType, "isDownloading");

    if (!blobData) {
      // warning modal
      return;
    }

    const blob = blobData.blob;

    if (blob && blob.size > 0) {
      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob);

      link.download = blobData.fileName;
      link.click();
    } else {
      // warning modal
    }
  };

  // Opens the browser's native print dialog for the PDF report, without
  // saving a file or navigating away from the page.
  const printFile = async () => {
    const blobData = await downloadPdfBlob("PDF", "isPrinting");

    if (!blobData) {
      // warning modal
      return;
    }

    const blob = blobData.blob;

    if (!blob || blob.size === 0) {
      // warning modal
      return;
    }

    // Force the "application/pdf" MIME type regardless of what blob storage
    // reports -- browsers only render a blob inline (and allow printing it)
    // when they recognize the type; anything else gets downloaded instead.
    const pdfBlob =
      blob.type === "application/pdf"
        ? blob
        : blob.slice(0, blob.size, "application/pdf");

    const blobUrl = URL.createObjectURL(pdfBlob);
    const iframe = document.createElement("iframe");
    iframe.style.display = "none";
    iframe.src = blobUrl;

    // Clean up the hidden iframe and object URL once the print dialog closes.
    // Fall back to a timeout since not every browser fires "afterprint" here.
    let cleanedUp = false;
    const cleanup = () => {
      if (cleanedUp) return;
      cleanedUp = true;
      document.body.removeChild(iframe);
      URL.revokeObjectURL(blobUrl);
    };

    iframe.onload = () => {
      iframe.contentWindow?.focus();
      iframe.contentWindow?.print();
      iframe.contentWindow?.addEventListener("afterprint", cleanup);
      setTimeout(cleanup, 60000);
    };

    document.body.appendChild(iframe);
  };

  return {
    downloadFile,
    printFile,
    getReportInfoData,
    showDICOMButton,
    showPNGButton,
    fileInfo,
    reportInfoError,
  };
};

export default useReportGeneration;
